package org.gymly.util

import org.gymly.api.ApiException
import org.gymly.i18n.ActiveLocale
import org.gymly.i18n.alertStrings

/**
 * Extrae el detalle útil del cuerpo de error del API.
 * Devuelve códigos `error.*` cuando aplica para que [userMessage] los traduzca al idioma activo.
 */
fun String.sanitizeApiErrorBody(): String {
    val trimmed = trim()
    // Código i18n del backend (ProblemDetail property "code")
    Regex(""""code"\s*:\s*"((?:\\.|[^"\\])*)"""")
        .find(trimmed)
        ?.groupValues
        ?.getOrNull(1)
        ?.replace("\\\"", "\"")
        ?.takeIf { it.startsWith("error.") }
        ?.let { return it }
    // ProblemDetail JSON del backend Spring (preferir detail)
    Regex(""""detail"\s*:\s*"((?:\\.|[^"\\])*)"""")
        .find(trimmed)
        ?.groupValues
        ?.getOrNull(1)
        ?.replace("\\\"", "\"")
        ?.let { detail ->
            return when {
                detail.equals("Invalid credentials", ignoreCase = true) ->
                    "error.auth.invalid_credentials"
                detail.equals("Unexpected error", ignoreCase = true) ->
                    "error.server.unexpected"
                detail.contains("timeout", ignoreCase = true) ->
                    if (detail.contains("adb", ignoreCase = true)) {
                        "error.net.timeout_adb"
                    } else {
                        "error.net.timeout"
                    }
                else -> detail
            }
        }
    Regex(""""error"\s*:\s*"([^"]+)"""")
        .find(trimmed)
        ?.groupValues
        ?.getOrNull(1)
        ?.let { err ->
            val status = Regex(""""status"\s*:\s*(\d+)"""").find(trimmed)?.groupValues?.getOrNull(1)?.toIntOrNull()
            return when {
                status == 403 || err.equals("Forbidden", ignoreCase = true) ->
                    "error.auth.forbidden"
                status == 401 || err.equals("Unauthorized", ignoreCase = true) ->
                    "error.auth.unauthorized"
                else -> err
            }
        }
    if (trimmed.contains("trying to proxy", ignoreCase = true) ||
        trimmed.contains("ECONNREFUSED", ignoreCase = true) ||
        trimmed.contains("proxy error", ignoreCase = true)
    ) {
        return "error.net.proxy"
    }
    if (trimmed.contains("<html", ignoreCase = true) || trimmed.contains("<!DOCTYPE", ignoreCase = true)) {
        val pre = Regex(
            "<pre>([\\s\\S]*?)</pre>",
            RegexOption.IGNORE_CASE,
        ).find(trimmed)?.groupValues?.get(1)?.trim()
        if (pre != null) {
            if (pre.contains("Cannot POST", ignoreCase = true) || pre.contains("Cannot GET", ignoreCase = true)) {
                return "error.net.api_not_found"
            }
            return pre.take(300)
        }
        if (trimmed.contains("502", ignoreCase = true) || trimmed.contains("Bad Gateway", ignoreCase = true)) {
            return "error.net.bad_gateway"
        }
        return "error.net.html_response"
    }
    return trimmed.take(500)
}

/**
 * Mensaje de alerta para el usuario en el idioma activo ([ActiveLocale]).
 * Los códigos `error.*` y textos conocidos se traducen; si no hay match, se usa el fallback.
 */
fun Throwable.userMessage(fallback: String): String {
    if (this is org.gymly.model.AdminPendingApprovalException) {
        return message ?: fallback
    }
    val alerts = alertStrings(ActiveLocale.current)
    val raw: String = when (this) {
        is ApiException -> message?.sanitizeApiErrorBody()?.takeIf { it.isNotBlank() } ?: fallback
        else -> message?.let { m ->
            when {
                m.contains("trying to proxy", ignoreCase = true) ||
                    m.contains("ECONNREFUSED", ignoreCase = true) ->
                    "error.net.proxy"
                m.contains("timeout", ignoreCase = true) ||
                    m.contains("Fail to fetch", ignoreCase = true) ||
                    m.contains("Failed to fetch", ignoreCase = true) ->
                    "error.net.timeout"
                else -> m.takeIf { it.isNotBlank() }
            }
        } ?: fallback
    }
    return alerts.localize(raw).ifBlank { alerts.localize(fallback).ifBlank { fallback } }
}
